import React, { useState } from "react";
import { BsThreeDotsVertical } from "react-icons/bs";
import {
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import styled from "styled-components";
import { CommonModal } from "../../components";
import { Notify, Utils } from "../../utils";
import { ExpenseService } from "../../_service/ExpenseService";
import moment from "moment";

const Card = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff;
  padding: 16px;
  border: 1px solid #dddddd;
  border-radius: 8px;
  margin-bottom: 12px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    /* transform: translateY(-2px); */
    border-color: #0066cc;
  }
`;

const Left = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const Avatar = styled.div<{ bg?: string }>`
  background: ${({ bg }) => bg || "#0066cc"};
  color: gray;
  font-weight: bold;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
`;

const TextPart = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Title = styled.div`
  font-weight: 600;
`;

const Description = styled.div`
  font-size: 13px;
  color: #444;
`;

const Tag = styled.span<{ status: string }>`
  display: inline-block;
  font-size: 10px;
  padding: 3px 6px;
  border-radius: 4px;
  background-color: ${({ status }) =>
    status === "approved"
      ? "#33865D"
      : status === "declined"
      ? "#F3B6BE"
      : status === "draft"
      ? "#FCD3A6"
      : "#e2e3e5"};
  color: ${({ status }) =>
    status === "approved"
      ? "#ffffff"
      : status === "declined"
      ? "#721c24"
      : status === "draft"
      ? "#856404"
      : "#383d41"};
  text-transform: capitalize;
  margin-top: 2px;
`;

const Subtitle = styled.div`
  font-size: 12px;
  color: #777;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Amount = styled.div`
  font-weight: bold;
  font-size: 16px;
  color: #333;
`;
const Dollar = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  border: 2px solid #999999;
  border-radius: 50%;

  img {
    width: 100%;
    object-fit: contain;
  }
`;

const CreatedAt = styled.div`
  font-size: .75rem;
  color: #999;
  font-style: italic;
  margin-top: 2px;
`;

type ExpenseCardProps = {
  data: {
    id: number;
    purchase_amount: number;
    description: string;
    spent_at: string;
    spent_on: string;
    created_at: string; 
    status: string;
    is_reimbursable: boolean;
    employee: {
      name: string;
    };
  };
  bgColor?: string;
};

const ExpenseCard: any = ({
  data,
  bgColor,
  refetchOwnExpenseClaims,
  openExpense,
}: any) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleDeleteConfirm = async () => {
    
    console.log("Delete confirmed for expense:", data?.id);

    try {
      let res = await ExpenseService._deleteExpenseClaim(data?.id);

      if (res?.status >= 200 && res?.status < 300) {
        Notify("Expense deleted successfully", 1);
        refetchOwnExpenseClaims();
      }
    } catch (error: any) {
      Utils.showErrorMessage(error);
    }

    setIsOpen(false);
  };

  return (
    <Card onClick={() => openExpense(data)}>
      <Left>
        <Avatar bg={bgColor}>
          {data?.employee?.name
            ?.split(" ")
            .map((n: any) => n?.[0])
            .join("")}
        </Avatar>
        <Dollar>
          <img src="/Pay Runs.svg" alt="Dollar Icon" />
        </Dollar>
        <TextPart>
          <InfoRow>
            <Title>{data?.spent_at}</Title>
            <Description>"{data?.description}"</Description>
            <Tag status={data?.status}>{data?.status}</Tag>
          </InfoRow>
          <Subtitle>
            {data?.employee.name} ·{" "}
            {new Date(data?.spent_on).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
            })}{" "}
            ·{" "}
            {data?.is_reimbursable ? (
              "Reimbursable"
            ) : (
              <span style={{ color: "#FF0000" }}>Non-reimbursable</span>
            )}
          </Subtitle>
          
          <CreatedAt>
            Created: {moment(data?.created_at).format("DD MMM YYYY, hh:mm A")}
          </CreatedAt>
        </TextPart>
      </Left>
      <Right>
        <Amount>
          {Utils.getCommanSepartedNumericStringWithDollar(data?.purchase_amount)}
        </Amount>
        <IconButton
          onClick={(event) => {
            event.stopPropagation(); // Prevent event bubbling to parent Card
            setAnchorEl(event.currentTarget);
          }}
          size="small"
        >
          <BsThreeDotsVertical color="#999999" size={20} />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          onClick={(event) => event.stopPropagation()}
        >
          <MenuItem
            disabled={data?.status != "draft"}
            onClick={(event) => {
              event.stopPropagation(); // Prevent event bubbling
              setIsOpen(true);
              setAnchorEl(null);
            }}
          >
            Delete
          </MenuItem>
        </Menu>

        <CommonModal
          handleClose={() => setIsOpen(false)}
          open={isOpen}
          updateData={handleDeleteConfirm}
          body={`Are you sure you want to delete "${data?.spent_at}" expense?`}
          heading="Confirm Expense Deletion"
          conformButtonText="Yes"
          cancelButtonText="No"
          onCancel={() => setIsOpen(false)}
        />
      </Right>
    </Card>
  );
};



export default ExpenseCard;
